/**
 * useSpecialEditorHost — CC-3 special-editor OPEN/CLOSE controller.
 *
 * Owns which entity's special editor is currently open and re-resolves the
 * typed `DrawingEntity` from the LIVE geometry document, so:
 *   - after a write-back the freshly patched entity (a new object identity)
 *     flows into the form and it re-derives its baseline (dirty resets), and
 *   - if the entity is deleted while the editor is open, the editor drops.
 *
 * Mirrors the `useDimStyleEditorDialog` ownership split from the App.tsx
 * decomposition: the HOOK owns the open/close state + store binding; the
 * `AppSpecialEditorDialogs` mount component is pure and renders from the result.
 *
 * `open(entityId)` is GATED on the entity kind actually declaring
 * `requiresSpecialEditor: true` (`getEntityPropertyCapabilities`) — a Line /
 * Circle / Text edit belongs in the plain Properties grid and must not open the
 * special-editor host. A missing entity is a silent no-op.
 *
 * ## Drawing-scoped identity
 *
 * An entity id is unique only WITHIN a drawing — the DXF bridge stamps ids from
 * DXF HANDLES (`dxfBridge.spaces.ts`), which repeat across files. So the open
 * target is scoped by the drawing's `documentEpoch` (the store's monotonic,
 * never-repeating document-swap counter — re-minted on reset / OPEN /
 * import-replacing, but NOT on an INSERT-merge into the same drawing). The
 * editor resolves its entity ONLY while the live drawing is still the one it was
 * opened against; a document swap (even to a drawing that reuses the same id for
 * an unrelated entity) collapses `entity` to null and closes the editor, so an
 * Apply can never write the old draft to an unrelated entity in another drawing.
 *
 * ## Function dependency map
 *
 * useSpecialEditorHost() -> { openEntityId, entity, open, close }
 *   getEntityById()                  -> state.editorStore (one-truth resolver)
 *   getEntityPropertyCapabilities()  -> @dxf-ai-editor/command-engine
 *   useEditorStore(geometryDocument) -> reactive re-resolve after each dispatch
 *   useEditorStore(documentEpoch)    -> drawing-swap guard (drop on swap)
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { getEntityPropertyCapabilities } from "@dxf-ai-editor/command-engine";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import { getEntityById, useEditorStore } from "../state/editorStore";

export interface UseSpecialEditorHostResult {
  /** id of the entity whose special editor is open, or `null` when closed. */
  openEntityId: string | null;
  /** the resolved typed entity — `null` when closed OR the id no longer
   *  resolves in the live document (e.g. the entity was deleted). */
  entity: DrawingEntity | null;
  /** open the special editor for an entity id. No-op when the entity is absent
   *  or its kind does not require a special editor (`requiresSpecialEditor`). */
  open: (entityId: string) => void;
  /** close the special editor. */
  close: () => void;
}

/** The drawing-scoped open target: an entity id is only meaningful inside the
 *  drawing it was resolved in, so it is paired with that drawing's
 *  `documentEpoch`. `null` when the editor is closed. */
interface OpenTarget {
  /** the `revisions.documentEpoch` of the drawing the editor was opened in. */
  documentEpoch: number;
  /** the (drawing-local, handle-derived) entity id. */
  entityId: string;
}

export function useSpecialEditorHost(): UseSpecialEditorHostResult {
  const [openTarget, setOpenTarget] = useState<OpenTarget | null>(null);
  const openEntityId = openTarget?.entityId ?? null;

  // Subscribe to the document so `entity` re-resolves on every dispatch (the
  // reference changes each mutation). `getEntityById` itself is a non-reactive
  // one-truth module resolver; the subscription is what drives reactivity.
  const geometryDocument = useEditorStore((store) => store.geometryDocument);
  // Subscribe to the drawing-swap counter so a document OPEN / reset / replace
  // (which re-mints `documentEpoch`) re-runs the guard below and drops a target
  // resolved against the PREVIOUS drawing.
  const documentEpoch = useEditorStore((store) => store.revisions.documentEpoch);

  const entity = useMemo<DrawingEntity | null>(() => {
    if (!openTarget) return null;
    // Drawing-swap guard: an entity id is unique only WITHIN a drawing. Resolve
    // ONLY while the live drawing is still the one the editor was opened in — a
    // swap re-mints `documentEpoch`, so a mismatch means a different drawing is
    // loaded (even one that reuses the same id for an unrelated entity).
    if (documentEpoch !== openTarget.documentEpoch) return null;
    return getEntityById(openTarget.entityId) ?? null;
    // `geometryDocument` is the in-drawing reactivity trigger (intentionally in
    // deps even though `getEntityById` reads it via the store, not the closure).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTarget, documentEpoch, geometryDocument]);

  // CLEAR the open target once its entity no longer resolves — because it was
  // deleted from the live drawing OR the drawing itself changed (the swap guard
  // above nulled `entity`). The memo already stops the dialog rendering, but
  // `openTarget` would otherwise stay SET, leaving two latent bugs: (1) a later
  // undo/import restoring an entity with the SAME id would spontaneously RE-OPEN
  // the editor with no user action, and (2) callers reading `openEntityId` would
  // see the editor "open" while nothing is shown. Clearing converges in one
  // extra render (target→null ⇒ entity stays null ⇒ guard false), so there is no
  // re-render loop.
  useEffect(() => {
    if (openTarget !== null && entity === null) {
      setOpenTarget(null);
    }
  }, [openTarget, entity]);

  const open = useCallback((entityId: string) => {
    const target = getEntityById(entityId);
    if (!target) return;
    if (!getEntityPropertyCapabilities(target.type).requiresSpecialEditor) return;
    // Stamp the CURRENT drawing's epoch so a later document swap drops this target.
    setOpenTarget({
      documentEpoch: useEditorStore.getState().revisions.documentEpoch,
      entityId,
    });
  }, []);

  const close = useCallback(() => setOpenTarget(null), []);

  return { openEntityId, entity, open, close };
}
